import React, { useState, useMemo } from 'react';
import { Box, Typography, IconButton, useTheme, alpha } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ContractExecutionStepsDisplay from './ContractExecutionStepsDisplay';
import DocumentEditSuggestion from './DocumentEditSuggestion';
import DocumentReviewSuggestions from './DocumentReviewSuggestions';

const ContractMessage = ({ message, isLast }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [copied, setCopied] = useState(false);

  const isAssistant = message.sender === 'bot' || message.role === 'assistant';
  const isStreaming = message.is_streaming;
  const content = message.content || message.text || '';

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const markdownComponents = useMemo(() => ({
    p: ({ children }) => (
      <Typography component="p" sx={{ my: 0.6, fontSize: 'inherit', lineHeight: 'inherit', '&:first-of-type': { mt: 0 }, '&:last-of-type': { mb: 0 } }}>
        {children}
      </Typography>
    ),
    h1: ({ children }) => (
      <Typography component="h1" sx={{ mt: 1.5, mb: 0.75, fontWeight: 700, fontSize: '0.95rem' }}>
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography component="h2" sx={{ mt: 1.25, mb: 0.5, fontWeight: 600, fontSize: '0.875rem' }}>
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography component="h3" sx={{ mt: 1, mb: 0.5, fontWeight: 600, fontSize: '0.84rem' }}>
        {children}
      </Typography>
    ),
    ul: ({ children }) => (
      <Box component="ul" sx={{ pl: 3, my: 0.6, listStyleType: 'disc', '& li': { mb: 0.3 } }}>{children}</Box>
    ),
    ol: ({ children }) => (
      <Box component="ol" sx={{ pl: 3, my: 0.6, listStyleType: 'decimal', '& li': { mb: 0.3 } }}>{children}</Box>
    ),
    li: ({ children }) => (
      <Box component="li" sx={{ mb: 0.3, display: 'list-item' }}>{children}</Box>
    ),
    strong: ({ children }) => (
      <Box component="strong" sx={{ fontWeight: 700 }}>{children}</Box>
    ),
    code: ({ inline, children }) => {
      if (inline) {
        return (
          <Box
            component="code"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, isDark ? 0.15 : 0.1),
              color: theme.palette.primary.main,
              px: 0.6,
              py: 0.15,
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.82em',
            }}
          >
            {children}
          </Box>
        );
      }
      return (
        <Box
          component="pre"
          sx={{
            bgcolor: isDark ? alpha('#000', 0.4) : alpha('#000', 0.04),
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            p: 1.25,
            borderRadius: '6px',
            overflow: 'auto',
            my: 0.75,
          }}
        >
          <Box component="code" sx={{ fontFamily: 'monospace', fontSize: '0.82em' }}>
            {children}
          </Box>
        </Box>
      );
    },
    table: ({ children }) => (
      <Box sx={{ overflowX: 'auto', my: 0.75 }}>
        <Box
          component="table"
          sx={{
            borderCollapse: 'collapse',
            width: '100%',
            fontSize: '0.82rem',
            '& th, & td': {
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              px: 1,
              py: 0.5,
              textAlign: 'left',
            },
            '& th': {
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              fontWeight: 600,
            },
          }}
        >
          {children}
        </Box>
      </Box>
    ),
    a: ({ href, children }) => (
      <Box
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: theme.palette.primary.main,
          textDecoration: 'underline',
          textDecorationColor: alpha(theme.palette.primary.main, 0.4),
          '&:hover': { textDecorationColor: theme.palette.primary.main },
        }}
      >
        {children}
      </Box>
    ),
    blockquote: ({ children }) => (
      <Box
        component="blockquote"
        sx={{
          borderLeft: `3px solid ${alpha(theme.palette.primary.main, 0.6)}`,
          pl: 1.5,
          my: 0.75,
          color: theme.palette.text.secondary,
          fontStyle: 'italic',
        }}
      >
        {children}
      </Box>
    ),
    hr: () => (
      <Box component="hr" sx={{ border: 'none', borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`, my: 1.25 }} />
    ),
  }), [theme, isDark]);

  const hasExecutionData =
    (message.node_executions && message.node_executions.length > 0) ||
    (message.execution_plan && message.execution_plan.steps && message.execution_plan.steps.length > 0) ||
    (message.tool_executions && message.tool_executions.length > 0) ||
    (message.activeTools && message.activeTools.length > 0) ||
    (message.completedTools && message.completedTools.length > 0);

  // ── User message ──
  if (!isAssistant) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, pl: 4 }}>
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderRadius: '12px 12px 3px 12px',
            backgroundColor: theme.palette.primary.main,
            maxWidth: '88%',
            boxShadow: `0 1px 4px ${alpha(theme.palette.primary.main, 0.3)}`,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.855rem',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              color: isDark ? alpha('#000', 0.87) : '#fff',
              fontWeight: 450,
            }}
          >
            {content}
          </Typography>
        </Box>
      </Box>
    );
  }

  // ── Assistant message ──
  return (
    <Box sx={{ mb: 1.75, pr: 3 }}>

      {/* Execution steps */}
      {hasExecutionData && (
        <ContractExecutionStepsDisplay
          message={{ ...message, sources: message.sources || [] }}
          isStreaming={isStreaming}
        />
      )}

      {/* Document edit suggestions */}
      {message.document_edits && message.document_edits.length > 0 && (
        <Box sx={{ mb: 1 }}>
          {message.document_edits.map((edit) => (
            <DocumentEditSuggestion
              key={edit.edit_id}
              edit={edit}
              messageId={message.id}
            />
          ))}
        </Box>
      )}

      {/* Document review (track-change suggestions from /document_review route) */}
      {((message.review_suggestions && message.review_suggestions.length > 0) ||
        (message.review_progress && message.review_progress.totalSections > 0)) && (
        <DocumentReviewSuggestions message={message} />
      )}

      {/* Message content bubble */}
      {content && (
        <Box
          sx={{
            px: 1.5,
            pt: 1.25,
            pb: 1,
            borderRadius: '3px 12px 12px 12px',
            backgroundColor: isDark
              ? alpha('#ffffff', 0.07)
              : alpha('#000000', 0.04),
            border: `1px solid ${isDark
              ? alpha('#ffffff', 0.1)
              : alpha('#000000', 0.07)}`,
            maxWidth: '100%',
          }}
        >
          <Box
            sx={{
              fontSize: '0.855rem',
              lineHeight: 1.7,
              color: theme.palette.text.primary,
              wordBreak: 'break-word',
            }}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {content}
            </ReactMarkdown>
            {isStreaming && (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  width: 6,
                  height: 13,
                  bgcolor: theme.palette.primary.main,
                  ml: 0.5,
                  borderRadius: '1px',
                  verticalAlign: 'text-bottom',
                  animation: 'cursorBlink 0.9s infinite',
                  '@keyframes cursorBlink': {
                    '0%, 45%': { opacity: 1 },
                    '55%, 100%': { opacity: 0 },
                  },
                }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Streaming dots — no content yet */}
      {isStreaming && !content && !hasExecutionData && (
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            px: 1.5,
            py: 1,
            borderRadius: '3px 12px 12px 12px',
            backgroundColor: isDark ? alpha('#ffffff', 0.07) : alpha('#000000', 0.04),
            border: `1px solid ${isDark ? alpha('#ffffff', 0.1) : alpha('#000000', 0.07)}`,
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 5,
                height: 5,
                borderRadius: '50%',
                backgroundColor: theme.palette.primary.main,
                animation: 'dotBounce 1.3s ease-in-out infinite',
                animationDelay: `${i * 0.18}s`,
                '@keyframes dotBounce': {
                  '0%, 60%, 100%': { transform: 'translateY(0)', opacity: 0.5 },
                  '30%': { transform: 'translateY(-5px)', opacity: 1 },
                },
              }}
            />
          ))}
        </Box>
      )}

      {/* Copy button */}
      {!isStreaming && content && (
        <Box sx={{ mt: 0.5, ml: 0.25 }}>
          <IconButton
            size="small"
            onClick={handleCopy}
            sx={{
              width: 24,
              height: 24,
              borderRadius: '6px',
              color: copied ? theme.palette.success.main : theme.palette.text.disabled,
              transition: 'all 0.15s',
              '&:hover': {
                color: copied ? theme.palette.success.main : theme.palette.text.secondary,
                backgroundColor: alpha(theme.palette.action.hover, 0.8),
              },
            }}
          >
            {copied
              ? <CheckIcon sx={{ fontSize: 13 }} />
              : <ContentCopyOutlinedIcon sx={{ fontSize: 13 }} />
            }
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default ContractMessage;
